import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import StatusBadge, { KONTAKT_STATUS } from '../components/StatusBadge';
import Modal from '../components/Modal';
import EmailModal from '../components/EmailModal';
import { hinweis } from '../components/Hinweise';

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('de-DE') : '–'; }

export default function Kontaktanfragen() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editBemerkung, setEditBemerkung] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [emailModal, setEmailModal] = useState(null);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (search) params.search = search;
      if (filter) params.status = filter;
      const res = await api.get('/api/admin/kontakt', { params });
      setRows(res.data.data);
      setTotal(res.data.pagination.total);
      setPages(res.data.pagination.pages || 1);
    } catch {
      setError('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [page, search, filter]);

  useEffect(() => { load(1); setPage(1); }, [search, filter]); // eslint-disable-line
  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  const openModal = (row) => {
    setSelected(row);
    setEditStatus(row.status);
    setEditBemerkung(row.bemerkung_intern || '');
    setDeleteConfirm(false);
  };

  const closeModal = () => { setSelected(null); setDeleteConfirm(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/kontakt/${selected.id}`, { status: editStatus, bemerkung_intern: editBemerkung });
      await load(page);
      closeModal();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) { setDeleteConfirm(true); return; }
    setSaving(true);
    try {
      await api.delete(`/api/admin/kontakt/${selected.id}`);
      await load(page);
      closeModal();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Kontaktanfragen <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '1rem' }}>({total})</span></h2>
      </div>

      <div className="card">
        <div className="toolbar">
          <input type="search" className="search-input" placeholder="Suche nach Name, E-Mail…"
            value={search} onChange={e => setSearch(e.target.value)} />
          <select className="filter-select" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">Alle Status</option>
            {Object.entries(KONTAKT_STATUS).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
          </select>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : rows.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">✉️</div><div className="empty-state-text">Keine Anfragen gefunden</div></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>Name</th><th>E-Mail</th><th>Betreff</th><th>Status</th><th>Eingang</th></tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} onClick={() => openModal(r)}>
                    <td className="td-muted">{r.id}</td>
                    <td style={{ fontWeight: 500 }}>{r.name}</td>
                    <td className="td-muted">{r.email}</td>
                    <td>{r.betreff || <span className="td-muted">(kein Betreff)</span>}</td>
                    <td><StatusBadge status={r.status} type="kontakt" /></td>
                    <td className="td-muted">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="pagination">
            <span className="pagination-info">{total} Einträge · Seite {page} von {pages}</span>
            <div className="pagination-btns">
              <button className="page-btn" onClick={() => setPage(p => Math.max(p-1,1))} disabled={page===1}>‹</button>
              {Array.from({length: Math.min(pages,7)},(_,i)=>i+1).map(p=>(
                <button key={p} className={`page-btn${p===page?' active':''}`} onClick={()=>setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" onClick={() => setPage(p => Math.min(p+1,pages))} disabled={page===pages}>›</button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <Modal title={`Kontaktanfrage #${selected.id} – ${selected.name}`} onClose={closeModal} size="md">
          <div className="detail-section">Nachricht</div>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Name</div><div className="detail-value">{selected.name}</div></div>
            <div className="detail-item"><div className="detail-label">E-Mail</div><div className="detail-value">{selected.email}</div></div>
            <div className="detail-item"><div className="detail-label">Betreff</div><div className="detail-value">{selected.betreff || '–'}</div></div>
            <div className="detail-item"><div className="detail-label">Eingang</div><div className="detail-value">{fmtDate(selected.created_at)}</div></div>
          </div>
          <div style={{ background: '#f4f7fb', borderRadius: 6, padding: '0.875rem', margin: '0.5rem 0 1rem', fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {selected.nachricht}
          </div>

          <div className="detail-section">Verwaltung</div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-control" value={editStatus} onChange={e => setEditStatus(e.target.value)}>
              {Object.entries(KONTAKT_STATUS).map(([v,c]) => <option key={v} value={v}>{c.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Interne Bemerkung</label>
            <textarea className="form-control" value={editBemerkung} onChange={e => setEditBemerkung(e.target.value)} rows={3} />
          </div>

          <div className="modal-actions">
            <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={saving}>
              {deleteConfirm ? 'Wirklich löschen?' : 'Löschen'}
            </button>
            {deleteConfirm && <button className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirm(false)}>Abbrechen</button>}
            {selected.email && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => setEmailModal(selected)}>
                ✉ E-Mail
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={closeModal}>Schließen</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </Modal>
      )}

      {emailModal && (
        <EmailModal
          an={emailModal.email}
          anzeigeName={emailModal.name}
          apiPfad={`/api/admin/kontakt/${emailModal.id}/email`}
          onClose={() => setEmailModal(null)}
          onSent={() => { load(page); }}
        />
      )}
    </div>
  );
}
